import React, { useEffect, useState, ChangeEvent } from 'react';
import { useWizardFormContext } from 'providers/WizardFormProvider';
import { Col, Form, Row } from 'react-bootstrap';
import { WizardFormData } from 'components/forms/CampaignWizardForm';
import {
  fetchHVAServiceNames,
  fetchHVACampaignTypes,
  fetchBXPTimeConditions,
  HVAServiceName,
  HVACampaignType,
  BXPTimeCondition
} from 'services/hvaservicecampaign.service';

const WizardCampaignDescForm: React.FC = () => {
  // eslint-disable-next-line
  const { formData, onChange, validation } = useWizardFormContext<WizardFormData>();
  const [services, setServices] = useState<HVAServiceName[]>([]);
  const [campaignTypes, setCampaignTypes] = useState<HVACampaignType[]>([]);
  const [timeSchedules, setTimeSchedules] = useState<BXPTimeCondition[]>([]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [servicesData, campaignTypesData, timeSchedulesData] =
          await Promise.all([
            fetchHVAServiceNames(),
            fetchHVACampaignTypes(),
            fetchBXPTimeConditions()
          ]);
        setServices(servicesData);
        setCampaignTypes(campaignTypesData);
        setTimeSchedules(timeSchedulesData);
      } catch (error) {
        console.error('Error loading form options:', error);
      }
    };

    loadOptions();
  }, []);

  useEffect(() => {
    // Si es una nueva campaña (sin ID), establecer 'active' como true por defecto
    if (!formData.id && formData.active === undefined) {
      onChange({
        target: {
          name: 'active',
          value: true
        }
        // eslint-disable-next-line
      } as any);
    }
  }, [formData.id, formData.active, onChange]);

  useEffect(() => {
    // Inicializar inbound_lookup_method si no tiene valor
    if (!formData.inbound_lookup_method) {
      onChange({
        target: {
          name: 'inbound_lookup_method',
          value: 'DATABASE'
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }
  }, []);

  const handleTimeScheduleChange = (e: ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLSelectElement;
    const selectedId = Number(target.value);
    // eslint-disable-next-line
    const selectedSchedule = timeSchedules.find(schedule => schedule.id === selectedId);
    onChange({
      target: {
        name: 'timeschedule',
        // eslint-disable-next-line
        value: selectedSchedule ? { id: selectedId, shortname: selectedSchedule.shortname } : { id: 0, shortname: '' }
      }
      // eslint-disable-next-line
    } as any);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      target: {
        name: e.target.name,
        value: e.target.checked
      }
      // eslint-disable-next-line
    } as any);
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      target: {
        name: e.target.name,
        value: e.target.value
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  };

  return (
    <>
      <p className="fs-9 text-primary">All fields are mandatory!</p>
      <Form.Group className="mb-3">
        <Form.Label>Campaign Name</Form.Label>
        <Form.Control
          type="text"
          name="name"
          placeholder="Enter campaign name"
          value={formData.name || ''}
          onChange={onChange}
          required={validation}
          maxLength={64}
        />
        <Form.Control.Feedback type="invalid">
          Campaign name is required.
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="description"
          placeholder="Enter campaign description"
          value={formData.description || ''}
          onChange={onChange}
          required={validation}
          maxLength={64}
        />
        <Form.Control.Feedback type="invalid">
          Campaign description is required.
        </Form.Control.Feedback>
      </Form.Group>

      <Row className="mb-3">
        <Col sm={4}>
          <Form.Group>
            <Form.Label>Service</Form.Label>
            <Form.Control
              as="select"
              name="serviceid"
              value={formData.serviceid || ''}
              onChange={onChange}
              required={validation}
            >
              <option value="">...</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              Service is required.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>Campaign Type</Form.Label>
            <Form.Control
              as="select"
              name="campaigntype"
              value={formData.campaigntype || ''}
              onChange={onChange}
              required={validation}
            >
              <option value="">...</option>
              {campaignTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.description}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              Campaign type is required.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>Time Schedule</Form.Label>
            <Form.Control
              as="select"
              name="timeschedule"
              value={formData.timeschedule?.id || ''}
              onChange={handleTimeScheduleChange}
              required={validation}
            >
              <option value="">...</option>
              {timeSchedules.map(schedule => (
                <option key={schedule.id} value={schedule.id}>
                  {schedule.shortname}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              Time Schedule is required.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mt-3">
        <Form.Label>Inbound calls, Authentication Lookup Method</Form.Label>
        <div className="rounded border p-3 mb-4 pb-2">
          <Form.Check
            type="radio"
            id="lookup-database"
            label="Database - Search customers in the database"
            name="inbound_lookup_method"
            value="DATABASE"
            checked={formData.inbound_lookup_method === 'DATABASE'}
            onChange={handleRadioChange}
          />
          <Form.Check
            type="radio"
            id="lookup-api"
            label="API - Search customers through external API"
            name="inbound_lookup_method"
            value="API"
            checked={formData.inbound_lookup_method === 'API'}
            onChange={handleRadioChange}
          />
          <Form.Text className="text-muted fs-10">
            This setting applies to both inbound and outbound campaigns
          </Form.Text>
        </div>
      </Form.Group>

      <Form.Group>
        <Form.Check
          type="switch"
          id="active-switch"
          label="Campaign Active"
          name="active"
          checked={formData.active === true}
          onChange={handleCheckboxChange}
        />
      </Form.Group>
    </>
  );
};

export default WizardCampaignDescForm;
